/* ============================================================
   CTS RFQ / QUOTE SYSTEM  (extracted from theme_edits.js)
   Self-contained: quote store, drawer, PDF generation.
   Load via theme.liquid:  {{ "cts-rfq.js" | asset_url | script_tag }}
   ============================================================ */

/* ============================================================
   RFQ / QUOTE SYSTEM
   ------------------------------------------------------------
   A parallel "quote" store that mirrors the cart's look and
   behavior but stays entirely separate from Shopify's checkout.
   Items live in localStorage and are rendered into a drawer
   that slides in from the right on desktop and up from the
   bottom on mobile, matching the cart.

   Public API (called from header RFQ button + product page
   "Add to quote" button):
     - window.QuoteRFQ.add(item)        add/merge a line item
     - window.QuoteRFQ.open()           open the drawer
     - window.QuoteRFQ.close()          close the drawer
     - window.QuoteRFQ.addVariant(variantId, quantity, productId)
                                        bridge from product page
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'cts_quote_items_v1';

  // Spec labels used to re-split a spec block onto one line per label.
  // Sorted longest-first so compound labels win (e.g. 'Connect Body Size:'
  // matches before 'Body Size:', 'Seal Type:' before 'Type:').
  var SPEC_LABELS = [
    'Method:', 'Type:', 'Body Style:', 'Seal Set:', 'Seal Range:', 'Seal Type:',
    'Seal Grade:', 'Number of Seals:', 'Bore Type:', 'Test Air Connection:',
    'Pilot Air Connection:', 'Construction Materials:', 'Test pressure:',
    'Pilot pressure:', 'Thread Size:', 'Accessory:', 'Body Size:',
    'Lead-in Size:', 'Connect Body Size:', 'ID Sealing Range:', 'OD Sealing Range:'
  ];
  var SPEC_SPLIT_RE = new RegExp(
    '([^\\n])\\s*(' +
    SPEC_LABELS.slice().sort(function (a, b) { return b.length - a.length; })
      .map(function (l) { return l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); })
      .join('|') +
    ')', 'g'
  );

  // Normalize a spec block: re-split glued labels, trim lines, drop blanks.
  function normalizeSpec(raw) {
    if (raw && typeof raw !== 'string') raw = JSON.stringify(raw);
    raw = (raw || '').replace(SPEC_SPLIT_RE, '$1\n$2');
    return raw.split(/\r?\n/)
      .map(function (ln) { return ln.trim(); })
      .filter(function (ln) { return ln.length > 0; })
      .join('\n');
  }

  var QuoteRFQ = {
    items: [],

    /* ---------- persistence ---------- */
    load: function () {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        this.items = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(this.items)) this.items = [];
      } catch (e) {
        this.items = [];
      }
    },
    save: function () {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        /* storage full / unavailable — fail quietly */
      }
      this.render();
      this.renderCount();
    },

    /* ---------- mutations ---------- */
    // item: { variantId, productId, title, variantTitle, price, sku, image, url, quantity }
    add: function (item) {
      if (!item || !item.variantId) return;
      var configuration = item.configuration || '';
      // Dedup key includes the configuration: two of the same variant with
      // DIFFERENT configurations are kept as separate quote lines; identical
      // configs (or none) merge by quantity as before.
      var key = String(item.variantId) + (configuration ? '::' + this.hashConfig(configuration) : '');
      var existing = this.items.find(function (i) { return i.key === key; });
      var qty = parseInt(item.quantity, 10) || 1;
      if (existing) {
        existing.quantity += qty;
      } else {
        this.items.push({
          key: key,
          variantId: String(item.variantId),
          productId: item.productId != null ? String(item.productId) : String(item.variantId),
          title: item.title || '',
          variantTitle: item.variantTitle || '',
          price: parseInt(item.price, 10) || 0,
          sku: item.sku || '',
          image: item.image || '',
          partNumber: item.partNumber || '',
          partDesc: item.partDesc || '',
          url: item.url || '',
          quantity: qty,
          configuration: configuration
        });
      }
      this.save();
      this.open();
    },

    // Short stable hash of a configuration string, for the dedup key.
    hashConfig: function (str) {
      var h = 0, i, chr;
      for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        h = ((h << 5) - h) + chr;
        h |= 0;
      }
      return String(h);
    },

    // Bridge from the product page button (reads QuoteProductData injected by product__add.liquid)
    addVariant: function (variantId, quantity, productId, configuration) {
      var pdata = (window.QuoteProductData || {})[productId];
      var variant = pdata && pdata.variants ? pdata.variants[String(variantId)] : null;
      if (!variant) {
        // Fall back to a minimal record so the add still works
        this.add({ variantId: variantId, productId: productId, quantity: quantity, configuration: configuration });
        return;
      }
      this.add({
        variantId: variantId,
        productId: productId,
        title: pdata.title,
        variantTitle: variant.title,
        price: variant.price,
        sku: variant.sku,
        image: variant.image,
        partNumber: pdata.partNumber || '',
        partDesc: pdata.partDesc || '',
        url: pdata.url || '',
        quantity: quantity,
        configuration: configuration
      });
    },

    changeQuantity: function (key, quantity) {
      var item = this.items.find(function (i) { return i.key === String(key); });
      if (!item) return;
      var q = parseInt(quantity, 10);
      if (isNaN(q) || q <= 0) {
        this.remove(key);
        return;
      }
      item.quantity = q;
      this.save();
    },

    remove: function (key) {
      this.items = this.items.filter(function (i) { return i.key !== String(key); });
      this.save();
    },

    count: function () {
      return this.items.reduce(function (sum, i) { return sum + (parseInt(i.quantity, 10) || 0); }, 0);
    },

    /* ---------- drawer open/close ---------- */
    open: function () {
      var root = document.getElementById('cts-rfq-drawer');
      if (!root) return;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cts-rfq-open');
      this.render();
      // Warm up the PDF library in the background so that, if the customer
      // checks "Download PDF" and submits, generation is near-instant and
      // completes before the contact form reloads the page.
      this.loadJsPDF(function () {});
    },
    close: function () {
      var root = document.getElementById('cts-rfq-drawer');
      if (!root) return;
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cts-rfq-open');
    },

    /* ---------- money formatting (reuse theme's Shopify.formatMoney) ---------- */
    money: function (cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }
      return '$' + ((parseInt(cents, 10) || 0) / 100).toFixed(2);
    },

    /* ---------- escape helper ---------- */
    esc: function (str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    /* ---------- header count badge ---------- */
    renderCount: function () {
      var count = this.count();
      document.querySelectorAll('.rfq-count').forEach(function (badge) {
        badge.hidden = count === 0;
        var num = badge.querySelector('.rfq-count-num');
        if (num) num.textContent = count;
      });
    },

    /* ---------- line-item markup (mirrors template__cart-item) ---------- */
    itemHtml: function (item) {
      var imgSrc = item.image ? (item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=200') : '';
      var variantLine = (item.variantTitle && item.variantTitle !== 'Default Title')
        ? '<a class="cts-rfq-item-variant" href="' + this.esc(item.url) + '" tabindex="-1">' + this.esc(item.variantTitle) + '</a>'
        : '';
      var skuLine = item.sku
        ? '<p class="cts-rfq-item-sku">' + this.esc(item.sku) + '</p>'
        : '';
      // Configuration spec (multi-line), shown beneath the SKU in the drawer.
      var configLine = item.configuration
        ? '<p class="cts-rfq-item-config" style="white-space:pre-line;">' + this.esc(item.configuration) + '</p>'
        : '';
      var linePrice = (parseInt(item.price, 10) || 0) * (parseInt(item.quantity, 10) || 1);

      return '' +
        '<div class="cts-rfq-item" data-key="' + this.esc(item.key) + '">' +
          '<a class="cts-rfq-item-image" href="' + this.esc(item.url) + '" tabindex="-1">' +
            (imgSrc ? '<img src="' + this.esc(imgSrc) + '" alt="' + this.esc(item.title) + '" width="200" height="200" loading="lazy">' : '') +
          '</a>' +
          '<div class="cts-rfq-item-details">' +
            '<a class="cts-rfq-item-title" href="' + this.esc(item.url) + '">' + this.esc(item.title) + '</a>' +
            variantLine +
            skuLine +
            configLine +
            '<div class="cts-rfq-item-actions">' +
              '<div class="cts-rfq-controls">' +
                '<div class="cts-rfq-qty">' +
                  '<button type="button" class="cts-rfq-qty-btn" data-action="dec" aria-label="Decrease quantity">&minus;</button>' +
                  '<input type="number" class="cts-rfq-qty-input" min="1" value="' + (parseInt(item.quantity, 10) || 1) + '" data-action="input" aria-label="Quantity">' +
                  '<button type="button" class="cts-rfq-qty-btn" data-action="inc" aria-label="Increase quantity">+</button>' +
                '</div>' +
                '<button type="button" class="cts-rfq-remove" data-action="remove" aria-label="Remove">' +
                  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6"/></svg>' +
                '</button>' +
              '</div>' +
              '<div class="cts-rfq-price">' + this.money(linePrice) + '</div>' +
            '</div>' +
          '</div>' +
        '</div>';
    },

    /* ---------- serialize items for the hidden form field / email ---------- */
    itemsAsText: function () {
      if (!this.items.length) return '(no items — general inquiry)';
      return this.items.map(function (i) {
        var parts = [];
        parts.push(i.quantity + 'x ' + (i.title || ''));
        if (i.variantTitle && i.variantTitle !== 'Default Title') parts.push('(' + i.variantTitle + ')');
        if (i.partNumber) parts.push('Part #: ' + i.partNumber);
        if (i.sku) parts.push('SKU: ' + i.sku);
        var head = parts.join(' ');
        // Append the configuration spec (indented) beneath the item line.
        if (i.configuration) {
          var cfg = i.configuration.split('\n').map(function (line) {
            return '    ' + line;
          }).join('\n');
          return head + '\n' + cfg;
        }
        return head;
      }).join('\n');
    },

    /* ---------- render drawer body ---------- */
    render: function () {
      // Contact form: the Message field is always required.
      var msg = document.getElementById('cts-rfq-message');
      var msgLabel = document.querySelector('label[for="cts-rfq-message"]');
      if (msg && !msg.getAttribute('required')) {
        msg.setAttribute('required', 'required');
        if (msgLabel && msgLabel.innerHTML.indexOf('cts-rfq-req') === -1) {
          msgLabel.innerHTML = 'Message<span class="cts-rfq-req">*</span>';
        }
      }
    },

    /* ---------- estimated total (sum of line prices) ---------- */
    total: function () {
      return this.items.reduce(function (sum, i) {
        return sum + ((parseInt(i.price, 10) || 0) * (parseInt(i.quantity, 10) || 0));
      }, 0);
    },

    /* ---------- PDF generation (browser-only, downloads to device) ---------- */
    // Lazy-load jsPDF from CDN, then run the callback.
    loadJsPDF: function (cb) {
      if (window.jspdf && window.jspdf.jsPDF) { cb(); return; }
      if (this._jspdfLoading) {
        var self = this;
        var t = setInterval(function () {
          if (window.jspdf && window.jspdf.jsPDF) { clearInterval(t); cb(); }
        }, 100);
        return;
      }
      this._jspdfLoading = true;
      var s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = function () { cb(); };
      s.onerror = function () {
        alert('Could not load the PDF generator. Please try again, or contact us directly.');
      };
      document.head.appendChild(s);
    },

    // Read a form field's value by id (empty string if missing).
    fieldVal: function (id) {
      var el = document.getElementById(id);
      return el && el.value ? String(el.value).trim() : '';
    },

    downloadPDF: function (done) {
      var self = this;
      this.loadJsPDF(function () { self.buildPDF(done); });
    },

    // Load an image URL and convert to a PNG data URL via canvas.
    // Resolves to { dataUrl, w, h } or null on failure (CORS/webp/etc).
    loadImageAsPng: function (url) {
      return new Promise(function (resolve) {
        if (!url) { resolve(null); return; }
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          try {
            var canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve({ dataUrl: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height });
          } catch (e) {
            resolve(null); // tainted canvas (CORS) — fall back
          }
        };
        img.onerror = function () { resolve(null); };
        img.src = url;
      });
    },

    buildPDF: function (done) {
      var self = this;
      var jsPDF = window.jspdf.jsPDF;
      var doc = new jsPDF({ unit: 'pt', format: 'letter' }); // 612 x 792 pt
      var pageW = doc.internal.pageSize.getWidth();
      var margin = 40;

      var LOGO_URL = 'https://cdn.shopify.com/s/files/1/0681/2975/1100/files/CTS-favicon.webp?v=1767046530';
      var CONTACT_EMAIL = 'sales@cincinnati-test.com';
      var CONTACT_PHONE = '513-367-6699';

      var moneyPlain = function (cents) {
        return '$' + ((parseInt(cents, 10) || 0) / 100).toFixed(2);
      };

      // Preload logo only (product images not used in this layout)
      this.loadImageAsPng(LOGO_URL).then(function (logoImg) {
        var y = margin;

        // ---- Top-left: logo ----
        var logoBottom = y;
        if (logoImg) {
          var logoW = 70;
          var logoH = logoImg.h ? (logoImg.h / logoImg.w) * logoW : 70;
          if (logoH > 70) { logoH = 70; logoW = logoImg.w ? (logoImg.w / logoImg.h) * logoH : 70; }
          try { doc.addImage(logoImg.dataUrl, 'PNG', margin, y, logoW, logoH); logoBottom = y + logoH; }
          catch (e) { logoImg = null; }
        }
        if (!logoImg) {
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text('Cincinnati Test Systems', margin, y + 16);
          logoBottom = y + 22;
        }

        y = logoBottom + 14;

        // ---- Thin blue line across the page ----
        doc.setDrawColor(0, 110, 185); // brand blue
        doc.setLineWidth(1.5);
        doc.line(margin, y, pageW - margin, y);
        doc.setLineWidth(1);
        y += 20;

        // ---- Bold disclaimer ----
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text('THIS IS NOT AN OFFICIAL QUOTE', pageW / 2, y, { align: 'center' });
        y += 22;

        // ---- Centered two-column To / Date table ----
        // Gather form values
        var first = self.fieldVal('cts-rfq-first-name');
        var last = self.fieldVal('cts-rfq-last-name');
        var name = (first + ' ' + last).trim();
        var company = self.fieldVal('cts-rfq-company');
        var street = self.fieldVal('cts-rfq-street');
        var city = self.fieldVal('cts-rfq-city');
        var state = self.fieldVal('cts-rfq-state');
        var postal = self.fieldVal('cts-rfq-postal');
        var country = self.fieldVal('cts-rfq-country');
        // City, ST Postal  (omit any pieces that are blank)
        var cityStateZip = [city, [state, postal].filter(Boolean).join(' ')]
          .filter(Boolean).join(', ');
        var addr = [street, cityStateZip, country].filter(Boolean);
        var email = self.fieldVal('cts-rfq-email');
        var phone = self.fieldVal('cts-rfq-phone');
        var dateStr = new Date().toLocaleDateString();

        // Build line arrays for each column
        var leftLines = [];
        leftLines.push({ t: 'To:', b: true });
        if (name) leftLines.push({ t: name });
        addr.forEach(function (a) { leftLines.push({ t: a }); });
        leftLines.push({ t: '' });
        if (name) leftLines.push({ t: 'Contact: ' + name });
        if (email) leftLines.push({ t: 'Email: ' + email });
        if (phone) leftLines.push({ t: 'Phone: ' + phone });

        var rightLines = [];
        rightLines.push({ t: 'Date: ' + dateStr });
        rightLines.push({ t: '' });
        rightLines.push({ t: 'Contact with questions or to request a quote:' });
        rightLines.push({ t: 'Email: ' + CONTACT_EMAIL });
        rightLines.push({ t: 'Phone: ' + CONTACT_PHONE });

        // Table geometry: centered, full content width, two equal columns
        var tableX = margin;
        var tableW = pageW - margin * 2;
        var colW = tableW / 2;
        var pad = 10;
        var lineH = 13;
        var rows = Math.max(leftLines.length, rightLines.length);
        var tableH = rows * lineH + pad * 2;
        var tableTop = y;

        // Border + center divider
        doc.setDrawColor(180);
        doc.rect(tableX, tableTop, tableW, tableH);
        doc.line(tableX + colW, tableTop, tableX + colW, tableTop + tableH);

        // Left column text
        doc.setFontSize(10);
        var ly = tableTop + pad + 9;
        leftLines.forEach(function (ln) {
          doc.setFont('helvetica', ln.b ? 'bold' : 'normal');
          if (ln.t) doc.text(ln.t, tableX + pad, ly);
          ly += lineH;
        });
        // Right column text
        var rry = tableTop + pad + 9;
        doc.setFont('helvetica', 'normal');
        var rightColW = colW - pad * 2;
        rightLines.forEach(function (ln) {
          if (ln.t) {
            var wrapped = doc.splitTextToSize(ln.t, rightColW);
            for (var w = 0; w < wrapped.length; w++) {
              doc.text(wrapped[w], tableX + colW + pad, rry + w * lineH);
            }
            rry += lineH * (wrapped.length > 1 ? wrapped.length : 1);
          } else {
            rry += lineH;
          }
        });

        y = tableTop + tableH + 24;

        // ---- Product table ----
        // Columns: Line | Part Number | Specifications | Qty | List Unit Price | Net Price
        var cLine = margin;
        var cPart = margin + 34;
        var cDesc = margin + 110;
        var cQty  = pageW - margin - 165;
        var cList = pageW - margin - 70;
        var cNet  = pageW - margin;        // right-aligned end

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Line', cLine, y);
        doc.text('Part Number', cPart, y);
        doc.text('Description', cDesc, y);
        doc.text('Qty', cQty, y, { align: 'right' });
        doc.text('List Unit Price', cList, y, { align: 'right' });
        doc.text('Net Price', cNet, y, { align: 'right' });
        y += 5;
        doc.setDrawColor(200);
        doc.line(margin, y, pageW - margin, y);
        y += 13;

        doc.setFont('helvetica', 'normal');
        var footerY = doc.internal.pageSize.getHeight() - 32; // safe zone above 'Page X of Y'
        var contTopMargin = margin + 30; // extra header space on continuation pages
        for (var i = 0; i < self.items.length; i++) {
          var it = self.items[i];
          var qty = parseInt(it.quantity, 10) || 1;
          var unit = parseInt(it.price, 10) || 0;
          var net = unit * qty;

          doc.setFontSize(9);

          // Part number (wrap)
          var partLines = doc.splitTextToSize(it.title || '-', cDesc - cPart - 6);

          // Description: use the configurator line-item if present, else the spec block
          var descSource = it.configuration && String(it.configuration).trim()
            ? it.configuration
            : it.partDesc;
          var specClean = normalizeSpec(descSource);
          var specLines = doc.splitTextToSize(specClean || '-', cQty - cDesc - 30);

          // Row height = tallest column; page-break BEFORE drawing if it won't fit
          var specLH = 9.5;
          var maxLines = Math.max(
            Array.isArray(partLines) ? partLines.length : 1,
            Array.isArray(specLines) ? specLines.length : 1,
            1
          );
          var rowHeight = (maxLines - 1) * specLH + 4 + 22;
          if (y + rowHeight > footerY) {
            // note at bottom of the page being left, then start a fresh page
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0);
            doc.text('Continued on next page', pageW / 2, footerY + 14, { align: 'center' });
            doc.addPage();
            y = contTopMargin;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
          }

          var rowTop = y;

          // Line number
          doc.text(String(i + 1), cLine, y);
          // Part number
          doc.text(partLines, cPart, y);
          // Specifications — draw each line at fixed 12pt spacing
          for (var sl = 0; sl < specLines.length; sl++) {
            doc.text(specLines[sl], cDesc, y + sl * specLH);
          }

          // Qty / List unit price / Net
          doc.text(String(qty), cQty, y, { align: 'right' });
          doc.text(moneyPlain(unit) + ' / 1', cList, y, { align: 'right' });
          doc.text(moneyPlain(net), cNet, y, { align: 'right' });

          // advance: bottom of last text line + padding
          var lastLineBottom = rowTop + (maxLines - 1) * specLH + 4; // ~baseline+descender
          y = lastLineBottom + 22; // total gap before next item

          // gray divider sits closer to line 1's text; larger gap below before line 2
          doc.setDrawColor(225);
          doc.setLineWidth(0.5);
          doc.line(margin, y - 13, pageW - margin, y - 13);
          doc.setLineWidth(1);
        }

        // ---- Estimated total (right side) ----
        if (y > 720) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0);
          doc.text('Continued on next page', pageW / 2, footerY + 14, { align: 'center' });
          doc.addPage();
          y = contTopMargin;
        }
        y += 10;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Estimated Total:', cList, y, { align: 'right' });
        doc.text(moneyPlain(self.total()), cNet, y, { align: 'right' });

        // ---- Page X of Y footer (bottom-right of every page) ----
        var pageCount = doc.internal.getNumberOfPages();
        var pageH = doc.internal.pageSize.getHeight();
        for (var p = 1; p <= pageCount; p++) {
          doc.setPage(p);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(120);
          doc.text('Page ' + p + ' of ' + pageCount, pageW - margin, pageH - 20, { align: 'right' });
        }
        doc.setTextColor(0);

        // ---- Save ----
        var fname = 'quote-' + new Date().toISOString().slice(0, 10) + '.pdf';
        doc.save(fname);
        if (typeof done === 'function') done();
      });
    },

    /* ---------- relocate the native Shopify form into the drawer ---------- */
    attachForm: function (attempt) {
      var self = this;
      var region = document.getElementById('cts-rfq-form-region');
      if (!region) return;

      // Already attached?
      if (region.querySelector('.cts-rfq-form-wrap')) return;

      var host = document.getElementById('cts-rfq-form-host');
      var formWrap = host ? host.querySelector('.cts-rfq-form-wrap') : null;

      if (!formWrap) {
        // Host may not be in the DOM yet (snippet renders later in the page).
        attempt = attempt || 0;
        if (attempt < 20) {
          setTimeout(function () { self.attachForm(attempt + 1); }, 150);
        } else {
          console.warn('[RFQ] contact form host not found. Did you add {% render "cts-rfq-form" %} to theme.liquid?');
        }
        return;
      }

      // Move it in and make sure it is visible (host parent was hidden).
      region.appendChild(formWrap);
      formWrap.removeAttribute('hidden');
      formWrap.style.display = 'block';

      var formEl = region.querySelector('form');
      if (formEl) {
        formEl.addEventListener('submit', function (e) {
          // Plain contact form: mark that this form was submitted (so the
          // success message shows only after a real submit), then let the
          // native submission proceed.
          try { sessionStorage.setItem('cts_rfq_just_submitted', '1'); } catch (err) {}
        });
      }

      console.log('[RFQ] contact form attached to drawer.');
    },

    // Fill the hidden fields, then let the form submit (email sends).
    // Optionally also generate the PDF in parallel.
    processSubmit: function (e, formEl, doPdf, doEmail) {
      var field = document.getElementById('cts-rfq-items-field');
      if (field) field.value = this.itemsAsText();
      var pdfFlag = document.getElementById('cts-rfq-pdf-flag');
      if (pdfFlag) pdfFlag.value = doPdf ? 'Yes' : 'No';

      // When a quote is actually being requested (email sending), prepend a
      // prominent marker to the top of the message body. Native contact emails
      // are plain text, so this is the most visible "first line" available.
      // Guard against double-prepending if this runs more than once.
      if (doEmail) {
        var msg = document.getElementById('cts-rfq-message');
        if (msg) {
          var marker = '*** QUOTE REQUESTED ***';
          var body = String(msg.value || '');
          if (body.indexOf(marker) !== 0) {
            msg.value = marker + (body ? '\n\n' + body : '');
          }
        }
      }

      if (doPdf) this.downloadPDF();

      // Flag the real submit so the success message shows only after this.
      try { sessionStorage.setItem('cts_rfq_just_submitted', '1'); } catch (err) {}

      // Let the submission proceed. If this was triggered outside a native
      // submit event, request the submit programmatically.
      this._submitResolved = true;
      if (e && typeof e.preventDefault === 'function') {
        // native submit already in flight — do nothing, it proceeds
      } else if (formEl) {
        if (formEl.requestSubmit) formEl.requestSubmit();
        else formEl.submit();
      }
    },

    // Download the PDF only; never send the email.
    doPdfOnly: function () {
      this.downloadPDF();
    },

    // Toggle the red "invalid" highlight on a checkbox row.
    markCheckboxRow: function (rowId, invalid) {
      var row = document.getElementById(rowId);
      if (!row) return;
      if (invalid) row.classList.add('cts-rfq-checkbox-invalid');
      else row.classList.remove('cts-rfq-checkbox-invalid');
    },

    // Show the browser's native validation bubble on a checkbox with a
    // custom message (matches how the other required fields report errors).
    showCheckboxBubble: function (box, message) {
      if (!box) return;
      box.setCustomValidity(message);
      if (box.reportValidity) {
        box.reportValidity();
      }
      // clear the custom message on next interaction so it can submit later
      var clearOnce = function () {
        box.setCustomValidity('');
        box.removeEventListener('change', clearOnce);
      };
      box.addEventListener('change', clearOnce);
    },

    /* ---------- build the drawer shell once ---------- */
    mount: function () {
      if (document.getElementById('cts-rfq-drawer')) return;
      var wrap = document.createElement('div');
      wrap.id = 'cts-rfq-drawer';
      wrap.className = 'cts-rfq-drawer';
      wrap.setAttribute('aria-hidden', 'true');
      wrap.innerHTML = '' +
        '<div class="cts-rfq-overlay" data-action="close"></div>' +
        '<aside class="cts-rfq-panel" role="dialog" aria-label="Contact Us">' +
          '<header class="cts-rfq-header">' +
            '<span class="cts-rfq-title">Contact Form</span>' +
            '<button type="button" class="cts-rfq-close" data-action="close" aria-label="Close">' +
              '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
            '</button>' +
          '</header>' +
          '<div class="cts-rfq-body" id="cts-rfq-body">' +
            '<div id="cts-rfq-form-region"></div>' +
          '</div>' +
        '</aside>';
      document.body.appendChild(wrap);

      // Relocate the native Shopify contact form into the drawer.
      this.attachForm();

      var self = this;

      // Delegated clicks inside the drawer
      wrap.addEventListener('click', function (e) {
        var actionEl = e.target.closest('[data-action]');
        if (!actionEl) return;
        var action = actionEl.getAttribute('data-action');

        if (action === 'close') { self.close(); return; }

        var itemEl = actionEl.closest('.cts-rfq-item');
        if (!itemEl) return;
        var key = itemEl.getAttribute('data-key');
        var item = self.items.find(function (i) { return i.key === key; });
        if (!item) return;

        if (action === 'inc') self.changeQuantity(key, item.quantity + 1);
        if (action === 'dec') self.changeQuantity(key, item.quantity - 1);
        if (action === 'remove') self.remove(key);
      });

      // Quantity typed directly
      wrap.addEventListener('change', function (e) {
        if (!e.target.classList.contains('cts-rfq-qty-input')) return;
        var itemEl = e.target.closest('.cts-rfq-item');
        if (!itemEl) return;
        self.changeQuantity(itemEl.getAttribute('data-key'), e.target.value);
      });

      // Esc closes
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.close();
      });
    },

    /* ---------- wire the header RFQ toggle button(s) ---------- */
    bindHeaderButtons: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var toggle = e.target.closest('[data-quote-toggle]');
        if (!toggle) return;
        e.preventDefault();
        var root = document.getElementById('cts-rfq-drawer');
        if (root && root.classList.contains('is-open')) {
          self.close();
        } else {
          self.open();
        }
      });
    },

    /* ---------- init ---------- */
    init: function () {
      this.load();
      this.mount();
      this.bindHeaderButtons();
      this.render();
      this.renderCount();

      // Shopify contact forms POST and reload the page. If the submit
      // The success message is server-rendered when posted_successfully? is
      // true — but that can read true on plain loads when a contact form
      // exists site-wide. Use a one-shot sessionStorage flag set at submit
      // time so the message shows exactly once, only after a real submit.
      var successMsg = document.getElementById('cts-rfq-form-success');
      var justSubmitted = false;
      try { justSubmitted = sessionStorage.getItem('cts_rfq_just_submitted') === '1'; } catch (err) {}

      if (successMsg && justSubmitted) {
        // consume the flag so a later reload won't show it again
        try { sessionStorage.removeItem('cts_rfq_just_submitted'); } catch (err) {}
        this.items = [];
        this.save();
        this.open();

        var closeBtn = document.getElementById('cts-rfq-success-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            if (successMsg.parentNode) successMsg.parentNode.removeChild(successMsg);
          });
        }
      } else if (successMsg) {
        // Server rendered it but this wasn't a fresh submit — remove the
        // stray message so it never shows on normal page loads.
        if (successMsg.parentNode) successMsg.parentNode.removeChild(successMsg);
      }
    }
  };

  window.QuoteRFQ = QuoteRFQ;

  // Back-compat alias for the product page button which calls window.Quote.addVariant(...)
  window.Quote = window.Quote || {};
  window.Quote.addVariant = function (variantId, quantity, productId, configuration) {
    QuoteRFQ.addVariant(variantId, quantity, productId, configuration);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { QuoteRFQ.init(); });
  } else {
    QuoteRFQ.init();
  }
})();